import { useEffect, useRef, useState } from 'react';
import {
  Instagram,
  Twitter,
  Facebook,
  Linkedin,
  ArrowUpRight,
} from 'lucide-react';
import logo from '@/assets/logo.jpeg';

function FooterSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <footer
      ref={sectionRef}
      className="relative overflow-hidden bg-[#080808] text-white py-10"
    >
      {/* Top Accent */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-QWA2_TALENT_SHOW-red via-QWA2_TALENT_SHOW-gold to-QWA2_TALENT_SHOW-red" />

      {/* Glow Effects */}
      <div className="absolute -top-24 left-0 w-72 h-72 bg-QWA2_TALENT_SHOW-red/10 blur-3xl rounded-full" />
      <div className="absolute -bottom-24 right-0 w-72 h-72 bg-QWA2_TALENT_SHOW-gold/10 blur-3xl rounded-full" />

      <div
        className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${
          isVisible
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-6'
        }`}
      >
        {/* Main Footer */}
        <div className="relative overflow-hidden rounded-[30px] border border-white/10 bg-white/5 backdrop-blur-2xl px-6 md:px-10 py-6">

          {/* Background Gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-QWA2_TALENT_SHOW-red/5 via-transparent to-QWA2_TALENT_SHOW-gold/5" />

          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">

            {/* Left */}
            <div className="flex items-center gap-5">
              <img
                src={logo}
                alt="Logo"
                className="w-16 h-16 rounded-2xl object-cover border border-white/10 shadow-lg"
              />

              <div>
                <h2 className="font-black text-2xl md:text-3xl leading-none">
                  QWAQWA
                </h2>

                <p className="bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold bg-clip-text text-transparent font-bold tracking-[0.2em] text-xs md:text-sm mt-1">
                  TALENT SHOW
                </p>
              </div>
            </div>

            {/* Center */}
            <div className="text-center max-w-xl">
              <p className="text-white/65 text-sm md:text-base leading-relaxed">
                Celebrating talent, creativity, and unforgettable performances
                across QwaQwa.
              </p>

              <a
                href="/competitions"
                className="group inline-flex items-center gap-2 mt-3 text-QWA2_TALENT_SHOW-gold text-sm font-semibold hover:gap-4 transition-all duration-300"
              >
                Explore competitions
                <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
              </a>
            </div>

            {/* Right */}
            <div className="flex items-center gap-3">
              {[
                { icon: Instagram, href: '#' },
                { icon: Twitter, href: '#' },
                { icon: Facebook, href: '#' },
                { icon: Linkedin, href: '#' },
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.href}
                  className="group w-11 h-11 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center hover:scale-110 hover:border-QWA2_TALENT_SHOW-gold/40 hover:bg-QWA2_TALENT_SHOW-gold/10 transition-all duration-300"
                >
                  <social.icon className="w-4 h-4 text-white/80 group-hover:text-QWA2_TALENT_SHOW-gold transition-colors duration-300" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-6 px-2">
          <p className="text-white/40 text-xs md:text-sm text-center md:text-left flex flex-wrap items-center justify-center md:justify-start gap-1">
  <span>© 2026 QwaQwa Talent Show. All rights reserved.</span>

  <span className="text-white/20">•</span>

  <span>
    Designed by{' '}
    <a
      href="https://eziyaway.co.za"
      target="_blank"
      rel="noopener noreferrer"
      className="font-semibold bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold bg-clip-text text-transparent hover:opacity-80 transition-opacity duration-300"
    >
      Eziyaway
    </a>
  </span>
</p>

          <div className="flex flex-wrap justify-center gap-5 text-xs md:text-sm">
            {['Privacy', 'Terms', 'Contact'].map((item, i) => (
              <a
                key={i}
                href="#"
                className="text-white/40 hover:text-QWA2_TALENT_SHOW-gold transition-colors duration-300"
              >
                {item}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default FooterSection;